// Disable no-re-export rule for entry point files
/* eslint-disable @atlaskit/editor/no-re-export */

// `./jsonTransformer` is a deprecated re-export shim (VOLTC-139); import from here instead.
import { JSONTransformer } from '@atlaskit/editor-json-transformer/JSONTransformer-2';
import { nodeToJSON } from '@atlaskit/editor-json-transformer';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import isEqual from 'lodash/isEqual';
import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import {
	findParentNodeClosestToPos,
	findParentNodeOfType,
} from '@atlaskit/editor-prosemirror/utils';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { FeatureToggles } from '../feature-keys';
import type { ContentStatistics } from '../utils/prosemirror-content-statistics';
import { ProseMirrorContentStatistics } from '../utils/prosemirror-content-statistics';

import type { FallbackState, IDMap, NonErrorFallbackState } from './pm-markdown/serializer';
import { MarkdownSerializerState } from './pm-markdown/serializer';
import type { MentionMap } from './types';

/**
 * Exporting the IDMap type so that future consumers of the transformer can be more type safe
 */
export type { IDMap } from './pm-markdown/serializer';

export type { MentionMap, FeatureToggles };

/**
 * If the first node is a paragraph we need to wrap it in a paragraph node for the markdown conversion to work properly
 */
function createTextWrapper(editorView: EditorView, fragment: PMNode | Fragment) {
	const { schema } = editorView.state;
	const { $from } = editorView.state.selection;
	const parent = findParentNodeClosestToPos($from, (node) => {
		return node.type.name === 'paragraph' || node.type.name === 'heading';
	})?.node;
	if (parent) {
		return schema.nodes.doc.createChecked(
			null,
			schema.nodes[parent.type.name].createChecked(parent.attrs, fragment),
		);
	} else {
		// If the node is a paragraph we need the parent to be a doc for the conversion to work correctly
		return schema.nodes.doc.createChecked(
			null,
			schema.nodes.paragraph.createChecked(null, fragment),
		);
	}
}

/**
 * This finds the closest list item parent to the current selection
 * Once found it will create a new list with the current fragment as its children
 */
function createListItemWrapper(editorView: EditorView, fragment: PMNode | Fragment) {
	const { schema } = editorView.state;
	const { $from } = editorView.state.selection;
	const parent = findParentNodeClosestToPos(
		$from,
		(node) => node.type.name === 'bulletList' || node.type.name === 'orderedList',
	)?.node;
	if (parent) {
		return schema.nodes[parent.type.name].createChecked(parent.attrs, fragment);
	}
	return fragment;
}

/**
 * If the first node is a task item we need to wrap it in a task list node for the markdown conversion to work properly
 */
function createTaskItemWrapper(schema: Schema, fragment: PMNode | Fragment) {
	return schema.nodes.doc.createChecked(null, schema.nodes.taskList.createChecked(null, fragment));
}

function createLayoutColumnWrapper(editorView: EditorView, fragment: PMNode | Fragment) {
	const { schema } = editorView.state;

	const parentSection = findParentNodeOfType(schema.nodes.layoutSection)(
		editorView.state.selection,
	);

	if (parentSection) {
		// Needs to be a doc for the serializer to work as it
		// loops through the fragments children to render
		return schema.nodes.doc.createChecked(
			null,
			schema.nodes.layoutSection.createChecked(parentSection.node.attrs, fragment),
		);
	}

	return fragment;
}

function isPMNode(node: PMNode | Fragment): node is PMNode {
	return 'type' in node;
}

/**
 * Sanitizes one top-level child via the canonical ProseMirror -> ADF transform (`JSONTransformer`),
 * e.g. renaming `panel_c1` back to `panel`. Only reconstructs the node if sanitizing actually
 * changed something, since rebuilding via JSON allocates new Mark instances that can otherwise
 * change mark-continuation behaviour (e.g. across a hardBreak) for content that didn't need it.
 * Falls back to the original node if sanitizing/rebuilding throws (some ProseMirror node shapes,
 * e.g. a test-only `unsupportedBlock` with no `originalValue`, can't round-trip through ADF JSON).
 */
function sanitizeChildForMarkdown(
	child: PMNode,
	transformer: JSONTransformer,
	schema: Schema,
): PMNode {
	try {
		const sanitizedJSON = transformer.encodeNode(child);
		if (isEqual(sanitizedJSON, nodeToJSON(child))) {
			return child;
		}
		return schema.nodeFromJSON(sanitizedJSON);
	} catch {
		return child;
	}
}

/**
 * Normalises ProseMirror-only schema constructs (e.g. `panel_c1`) to their ADF-equivalent shape
 * before markdown serialization, so they don't hit the "Token type ... not supported" fallback.
 * Gated by `markdownPlusSanitizeProseMirrorOnlyNodes`; a no-op otherwise.
 */
function sanitizeForMarkdown<T extends PMNode | Fragment>(
	node: T,
	featureToggles: FeatureToggles,
): T {
	if (!featureToggles.markdownPlusSanitizeProseMirrorOnlyNodes) {
		return node;
	}

	const firstChild = node.firstChild;
	if (!firstChild) {
		return node;
	}

	const schema = firstChild.type.schema;
	const transformer = new JSONTransformer(schema);

	let anyChildChanged = false;
	const sanitizedChildren: PMNode[] = [];
	node.forEach((child) => {
		const sanitizedChild = sanitizeChildForMarkdown(child, transformer, schema);
		anyChildChanged = anyChildChanged || sanitizedChild !== child;
		sanitizedChildren.push(sanitizedChild);
	});

	// Nothing needed sanitizing - keep the original node/fragment (and its child object identity)
	// rather than reconstructing an equivalent copy.
	if (!anyChildChanged) {
		return node;
	}

	const sanitizedFragment = Fragment.fromArray(sanitizedChildren);
	return (isPMNode(node) ? node.copy(sanitizedFragment) : sanitizedFragment) as T;
}

/**
 * This function handles partial selections and it will fix any incomplete fragments that won't be converted properly
 * If you do a partial selection, it will not select the parent node of certain nodes text, listItems etc so once it hits the serializer
 * the serializer will just fallback to plain text and not convert the node properly.
 *
 * This function checks the first child of the current selection, if its a node that needs a parent it will find the closest parent
 * and create a new fragment that will be converted properly by the serializer
 */
function checkFragment(editorView: EditorView, schema: Schema, fragment: PMNode | Fragment) {
	switch (fragment?.firstChild?.type?.name) {
		case 'text':
			return createTextWrapper(editorView, fragment);
		case 'listItem':
			return createListItemWrapper(editorView, fragment);
		case 'taskItem':
			return createTaskItemWrapper(schema, fragment);
		case 'layoutColumn':
			return createLayoutColumnWrapper(editorView, fragment);
		default:
			return fragment;
	}
}

/**
 * Converts a selection of nodes from a Prosemirror editor into Markdown.
 *
 * @param editorView - The EditorView instance of the current Prosemirror editor.
 * @param fragment - The ProseMirror node or fragment that is to be converted into Markdown.
 * @param featureToggles - Optional feature toggles to use for the conversion.
 * @param mentionMap - Optional mention map for serializing mentions.
 * @returns An object containing the converted markdown, the fallback state from the Markdown serializer, and the id map from the Markdown serializer.
 */
export function convertProsemirrorToMarkdown({
	editorView,
	fragment,
	featureToggles = {},
	mentionMap = {},
	idMap = {},
}: {
	editorView: EditorView;
	/**
	 * See {@link FeatureToggles} for available features
	 */
	featureToggles?: FeatureToggles;
	fragment: PMNode | Fragment;
	/**
	 * See {@link IDMap} for more information
	 */
	idMap?: IDMap;
	/**
	 * See {@link MentionMap} for more information
	 */
	mentionMap?: MentionMap;
}): {
	contentStatistics: ContentStatistics;
	fallbackState: FallbackState;
	idMap: IDMap;
	markdown: string;
	nonErrorFallbackState: NonErrorFallbackState;
} {
	fragment = checkFragment(editorView, editorView.state.schema, fragment);

	return convertProsemirrorNodeToMarkdown({
		node: fragment,
		featureToggles,
		mentionMap,
		idMap,
	});
}

/**
 * Converts a ProseMirror node or document node to markdown/ markdown plus.
 *
 * This function uses the `convertProsemirrorToMarkdown` implementation. Using a node instead of a
 * fragment means that we don't need access to the editorView or the schema to get the result.
 *
 * @param node - The ProseMirror node or document node that is to be converted into Markdown.
 * @param featureToggles - Optional feature toggles to use for the conversion.
 * @param mentionMap - Optional mention map for serializing mentions.
 * @returns An object containing the converted markdown, the fallback state from the Markdown serializer, and the id map from the Markdown serializer.
 */
export function convertProsemirrorNodeToMarkdown({
	node,
	featureToggles = {},
	mentionMap = {},
	idMap = {},
}: {
	/**
	 * See {@link FeatureToggles} for available features
	 */
	featureToggles?: FeatureToggles;
	idMap?: IDMap;
	/**
	 * See {@link MentionMap} for more information
	 */
	mentionMap?: MentionMap;
	node: PMNode | Fragment;
}): {
	contentStatistics: ContentStatistics;
	fallbackState: FallbackState;
	idMap: IDMap;
	markdown: string;
	nonErrorFallbackState: NonErrorFallbackState;
} {
	const sanitizedNode = sanitizeForMarkdown(node, featureToggles);

	const markdownSerializerState = new MarkdownSerializerState(featureToggles, mentionMap, idMap);
	markdownSerializerState.renderContent(sanitizedNode);

	const pmStats = new ProseMirrorContentStatistics(sanitizedNode);
	const contentStatistics = pmStats.collectStatistics();

	const markdown =
		markdownSerializerState.out === '\u200c'
			? ''
			: // Return empty string if editor only contains a zero-non-width character
				markdownSerializerState.out;

	return {
		markdown,
		fallbackState: markdownSerializerState.fallbackState,
		nonErrorFallbackState: markdownSerializerState.nonErrorFallbackState,
		idMap: markdownSerializerState.idMap,
		contentStatistics,
	};
}
